/**
 * @module Cognito
 *
 * AWS Cognito authentication operations. Wraps `amazon-cognito-identity-js`.
 *
 * ## Pure functions (fully testable — see src/__tests__/Cognito.test.ts)
 * - {@link determineRefreshAction} — token refresh decision tree; injectable clock
 * - {@link IDLE_TIMEOUT_SECONDS} — 3600 (1 hour idle before sign-out)
 * - {@link ABSOLUTE_TIMEOUT_SECONDS} — 36000 (10 hour absolute session limit)
 *
 * ## Main operations
 * - {@link authenticateUser} — email/password login
 * - {@link refreshToken} — token refresh with idle/absolute timeout enforcement
 * - {@link refreshTokenAsync} — promise wrapper for use in async contexts
 * - {@link signOut} — clears Cognito session + localStorage
 *
 * @see {@link module:CognitoTs} for the typed `decryptToken` utility
 */
// using https://www.npmjs.com/package/amazon-cognito-identity-js/v/2.0.30
// Backend: Cognito usernames are UUIDs; email is an alias attribute.
// Client secret not used — standard for browser-based Cognito apps (public client).

import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  IAuthenticationDetailsData,
  ISignUpResult,
  NodeCallback,
} from 'amazon-cognito-identity-js';
import { ExpiredSessionError } from '../shared/Error';
import { forceSignOut } from './Utils';
import { clearStorage } from '../utils/LogOutUtils';
import { storage } from '../services/StorageService';
import type { LocalStorageUser } from '../shared/types/appTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Bivariant callback type — allows callers to use more specific parameter types.
interface CognitoCallback {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (...args: any[]): void;
}
type RefreshAction = 'valid' | 'refresh' | 'expired' | 'idle';

// ---------------------------------------------------------------------------
// User pool singleton
// ---------------------------------------------------------------------------

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_USER_POOL_ID as string,
  ClientId: import.meta.env.VITE_CLIENT_ID as string,
});

// ---------------------------------------------------------------------------
// User management
// ---------------------------------------------------------------------------

export const createUser = (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  callback: NodeCallback<Error, ISignUpResult>,
): void => {
  const attributeList = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
    new CognitoUserAttribute({ Name: 'custom:firstName', Value: firstName }),
    new CognitoUserAttribute({ Name: 'custom:lastName', Value: lastName }),
  ];
  userPool.signUp(email, password, attributeList, [], callback);
};

export const createUserEmailOnly = (
  email: string,
  password: string,
  callback: NodeCallback<Error, ISignUpResult>,
): void => {
  const attributeList = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
  ];
  userPool.signUp(email, password, attributeList, [], callback);
};

export const verifyUser = (
  username: string,
  verifyCode: string,
  callback: CognitoCallback,
): void => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.confirmRegistration(verifyCode, true, callback);
};

// resendConfirmation: wires up CognitoUser.resendConfirmationCode.
export const resendConfirmation = (
  username: string,
  callback: CognitoCallback,
): void => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.resendConfirmationCode(callback);
};

export const changePassword = (
  _username: string,
  oldPassword: string,
  newPassword: string,
  callback: CognitoCallback,
): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return;
  try {
    cognitoUser.getSession((sessionErr: Error | null) => {
      if (sessionErr) console.warn('[Cognito] session error:', sessionErr);
    });
  } catch {
    console.warn('[Cognito] Could not get current session');
  }
  cognitoUser.changePassword(oldPassword, newPassword, callback);
};

export const sendResetPasswordCode = (
  username: string,
  callback: CognitoCallback,
): void => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.forgotPassword({
    onSuccess() {},
    onFailure(err: { code?: string; message?: string }) {
      console.warn('[Cognito] sendResetPasswordCode failed:', err);
      if (err.code === 'LimitExceededException')
        callback(
          {
            message:
              'Too many attempts to reset password.  Please try again in an hour.',
          },
          null,
        );
      else callback({ message: 'Password could not be reset' }, null);
    },
    inputVerificationCode() {
      callback(false, null);
    },
  });
};

export const resetPassword = (
  username: string,
  verificationCode: string,
  newPassword: string,
  callback: CognitoCallback,
): void => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.confirmPassword(verificationCode, newPassword, {
    onSuccess() {
      callback(null, { message: 'Password is reset' });
    },
    onFailure(err: { code?: string; message?: string }) {
      if (err.code === 'LimitExceededException')
        callback(
          {
            message:
              'Too many attempts to reset password.  Please try again in an hour.',
          },
          null,
        );
      else
        callback(
          { message: err.message ?? 'Password could not be reset' },
          null,
        );
    },
  });
};

export const authenticateUser = (
  email: string,
  password: string,
  callback: CognitoCallback,
): void => {
  if (!email || !password) {
    callback({ message: 'Please enter credentials' });
    return;
  }
  const authData: IAuthenticationDetailsData = {
    Username: email,
    Password: password,
  };
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails(authData);

  cognitoUser.authenticateUser(authDetails, {
    onSuccess: (result) => {
      callback(null, null, result);
    },
    onFailure: (err) => {
      callback(err);
    },
    newPasswordRequired: (userAttributes) => {
      // User was signed up by an admin and must provide new password
      callback(null, userAttributes, cognitoUser);
    },
  });
};

export const setNewPassword = (
  userAttributes: Record<string, unknown>,
  cognitoUser: CognitoUser,
  newPassword: string,
  callback: CognitoCallback,
): void => {
  // The API doesn't accept this field back
  delete userAttributes.email_verified;
  cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
    onSuccess: (result) => {
      callback(null, result);
    },
    onFailure: (err) => {
      callback(err);
    },
  });
};

export const signOut = (): void => {
  const currentUser = userPool.getCurrentUser();
  if (currentUser) currentUser.signOut();
  clearStorage();
};

// ---------------------------------------------------------------------------
// Token refresh — pure decision tree
// ---------------------------------------------------------------------------

export const IDLE_TIMEOUT_SECONDS = 3600; // 1 hour
export const ABSOLUTE_TIMEOUT_SECONDS = 36000; // 10 hours

/**
 * Pure decision tree for token refresh.
 *
 * Returns one of:
 *   'valid'    — token is still live, proceed without refresh
 *   'refresh'  — token expired but session is still active, refresh it
 *   'expired'  — absolute session timeout reached, sign out
 *   'idle'     — idle timeout reached, sign out
 */
export const determineRefreshAction = (
  tokenExpiration: number,
  signInExpiration: number,
  nowTimestamp: number,
  idleTimeout = IDLE_TIMEOUT_SECONDS,
): RefreshAction => {
  if (tokenExpiration >= nowTimestamp) return 'valid';
  if (signInExpiration < nowTimestamp) return 'expired';
  if (tokenExpiration + idleTimeout < nowTimestamp) return 'idle';
  return 'refresh';
};

// Throws ExpiredSessionError (rather than calling a callback) because the
// caller (refreshTokenAsync / useGet) can catch it as a promise rejection.
export const refreshToken = (
  username: string,
  _email: string,
  _idToken: string,
  _accessToken: string,
  existingRefreshToken: string,
  tokenExpiration: number,
  signInExpiration: number,
  callback: () => void,
): void => {
  // Token expiration uses Unix timestamps (UTC-agnostic). No timezone adjustment needed.
  // refreshSession can return NotAuthorizedException when the refresh token is
  // invalidated. signOut + forceSignOut is the correct response.
  const nowTimestamp = Math.floor(Date.now() / 1000);
  const action = determineRefreshAction(
    tokenExpiration,
    signInExpiration,
    nowTimestamp,
  );

  if (action === 'expired') {
    console.warn('[Cognito] session expired — signing out');
    signOut();
    throw new ExpiredSessionError(true);
  } else if (action === 'idle') {
    console.warn('[Cognito] session idled — signing out');
    signOut();
    throw new ExpiredSessionError(true);
  } else if (action === 'refresh') {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.refreshSession(
      new CognitoRefreshToken({ RefreshToken: existingRefreshToken }),
      (err: Error | null, session: CognitoUserSession) => {
        if (err) {
          console.warn('[Cognito] token refresh denied — signing out');
          signOut();
          forceSignOut();
        } else {
          const user = storage.getUser() ?? ({} as LocalStorageUser);
          user.idToken.jwtToken = session.getIdToken().getJwtToken();
          user.refreshToken.token = session.getRefreshToken().getToken();
          user.tokenExpiration = (
            JSON.parse(
              atob(session.getIdToken().getJwtToken().split('.')[1]),
            ) as { exp: number }
          ).exp;
          storage.setUser(user);
          callback();
        }
      },
    );
  } else {
    // token is still valid — proceed without refresh
    callback();
  }
};

/**
 * Promise wrapper around {@link refreshToken}.
 */
export const refreshTokenAsync = (
  username: string,
  email: string,
  idToken: string,
  accessToken: string,
  existingRefreshToken: string,
  tokenExpiration: number,
  signInExpiration: number,
): Promise<void> =>
  new Promise<void>((resolve) => {
    refreshToken(
      username,
      email,
      idToken,
      accessToken,
      existingRefreshToken,
      tokenExpiration,
      signInExpiration,
      resolve,
    );
  });

// ---------------------------------------------------------------------------
// Current user (not currently called in the application)
// ---------------------------------------------------------------------------

// NOTE: userPool.getCurrentUser() returns null when no Cognito session exists in
// localStorage. The null guard prevents a throw when no session is present.
export const getCurrentUser = (callback: CognitoCallback): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return;

  try {
    cognitoUser.getSession((sessionErr: Error | null) => {
      if (sessionErr) {
        console.warn('[Cognito] session error:', sessionErr);
      } else {
        cognitoUser.getUserAttributes((attributeErr, attributes) => {
          if (attributeErr) {
            console.warn('[Cognito] attribute error:', attributeErr);
          } else {
            callback(attributes);
          }
        });
      }
    });
  } catch {
    console.warn('[Cognito] user does not exist');
  }
};

// ---------------------------------------------------------------------------
// Token utilities (consolidated from CognitoTs.ts in 10.03)
// ---------------------------------------------------------------------------

import { Buffer } from 'buffer/';
import type { DecryptedUser } from '../shared/types/appTypes';

/** Decodes the payload section of a JWT token. */
export const decryptToken = (jwtToken: string): Buffer =>
  Buffer.from(jwtToken.split('.')[1], 'base64');

/**
 * Builds a LocalStorageUser from a Cognito auth response, stores it in
 * localStorage, and returns the decrypted user info.
 */
export const getDecryptedUserAndStoreCachedUser = (cognitoResponse: {
  accessToken: { jwtToken: string; payload: Record<string, unknown> };
  idToken: { jwtToken: string; payload: Record<string, unknown> };
  refreshToken: { token?: string; refreshToken?: string };
}): DecryptedUser => {
  let userStorage = {} as LocalStorageUser;
  userStorage = { ...cognitoResponse, ...userStorage };
  const userInfo = JSON.parse(
    decryptToken(cognitoResponse.idToken.jwtToken).toString(),
  ) as DecryptedUser;

  userStorage.tokenExpiration = userInfo.exp;
  userStorage.email = userInfo.email;
  userStorage.username = userInfo['cognito:username'];
  userStorage.signedIn = Math.floor(new Date().getTime() / 1000);
  userStorage.signInExpiration = 10 * 60 * 60 + userStorage.signedIn;
  if (cognitoResponse.refreshToken.refreshToken) {
    userStorage.refreshToken.token = cognitoResponse.refreshToken.refreshToken;
  }
  storage.setUser(userStorage);
  return userInfo;
};
